import React, { MouseEventHandler } from "react";
import { ContentSpacer } from "../common/layout";
import { useListTemplatesQuery } from "../../data/queries/list-templates";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Skeleton } from "../../components/ui/skeleton";
import { FileTextIcon, LayoutTemplateIcon, EllipsisIcon, Trash2Icon } from "lucide-react";
import { useOrg } from "../../auth/org-provider";
import { Link } from "react-router-dom";
import { DateTime } from "luxon";
import { ProjectTemplateListItem } from "tekkr-common/dist/model/project/project-template";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { useDeleteProjectTemplateMutation } from "../../data/mutations/delete-project-template";
import { useConfirmDialog } from "../../components/modals/confirmation-dialog-provider";

export function TemplatesPage() {
    const query = useListTemplatesQuery();
    const org = useOrg();

    return (
        <ContentSpacer>
            <div className={"mt-8 flex flex-row items-center justify-between gap-4"}>
                <div>
                    <h2 className="flex items-center gap-2">Project Templates</h2>
                    <p>Browse and use pre-built project templates to get started quickly.</p>
                </div>
            </div>

            <div className="mt-8">
                {query.isLoading && (
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {[1, 2, 3].map((i) => (
                            <Card key={i} className="p-4">
                                <Skeleton className="mb-2 h-6 w-3/4" />
                                <Skeleton className="mb-2 h-4 w-full" />
                                <Skeleton className="h-20 w-full" />
                            </Card>
                        ))}
                    </div>
                )}

                {query.isError && (
                    <div className="flex flex-col items-center gap-2 rounded-md border border-destructive bg-destructive/10 p-4 text-center">
                        <LayoutTemplateIcon className="mb-1 h-6 w-6 text-destructive" />
                        <h3 className="text-lg font-semibold text-destructive">Failed to load templates</h3>
                        <p className="text-sm text-muted-foreground">
                            There was an error loading project templates. Please try again later.
                        </p>
                    </div>
                )}

                {query.isSuccess && query.data.length === 0 && (
                    <div className={"flex w-full flex-col items-center gap-2 rounded-xl border p-8"}>
                        <LayoutTemplateIcon className={"h-6 w-6 text-muted-foreground"} />
                        <h3 className="text-lg font-semibold">No templates yet</h3>
                        <p className="text-center text-sm text-muted-foreground">
                            Templates will appear here once they are created.
                        </p>
                    </div>
                )}

                {query.isSuccess && query.data.length > 0 && (
                    <div className={"flex flex-col gap-3"}>
                        <p className={"text-sm font-semibold text-muted-foreground"}>
                            Available templates for {org.name}
                        </p>
                        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                            {query.data.map((template) => (
                                <TemplateCard key={template.id} template={template} />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </ContentSpacer>
    );
}

interface TemplateCardProps {
    template: ProjectTemplateListItem;
}

function TemplateCard({ template }: TemplateCardProps) {
    const deleteMutation = useDeleteProjectTemplateMutation();
    const confirm = useConfirmDialog();

    const handleDelete: MouseEventHandler<HTMLDivElement> = (e) => {
        e.preventDefault();
        confirm(
            {
                title: "Delete Template",
                body: (
                    <div className={"flex flex-col gap-3 pb-3"}>
                        <div>
                            Are you sure you want to delete the template{" "}
                            <strong className={"font-semibold"}>{template.name}</strong>?
                        </div>
                        <div className={"text-sm text-muted-foreground"}>This action cannot be undone.</div>
                    </div>
                ),
                confirmButtonText: "Delete",
                confirmButtonVariant: "destructive",
            },
            () => {
                deleteMutation.mutate(template.id);
            }
        )();
    };

    const createdAt = DateTime.fromISO(template.createdAt).toRelative();

    return (
        <Link to={`/templates/${template.id}`}>
            <Card className="flex h-full cursor-pointer flex-col gap-3 p-4 transition-all hover:scale-[99%] hover:bg-accent">
                <div className="flex items-start gap-3">
                    <div className="rounded-md bg-primary/10 p-2">
                        <FileTextIcon className="h-5 w-5 text-primary" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <h4 className="line-clamp-1 font-semibold">{template.name}</h4>
                        <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{template.description}</p>
                    </div>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                                <EllipsisIcon className="h-4 w-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={handleDelete} className={"text-destructive"}>
                                <Trash2Icon className="mr-2 h-4 w-4" />
                                Delete
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
                <div className="mt-auto flex items-center justify-between text-xs text-muted-foreground">
                    <span>Created {createdAt}</span>
                    <Button variant="ghost" size="sm" className="h-7 text-xs" asChild>
                        <span>View template</span>
                    </Button>
                </div>
            </Card>
        </Link>
    );
}
